/*
 * Copyright (C) 2025-2026 The Klangmotor Authors (see AUTHORS.MD)
 * Portions derived from tonal.js — Copyright (c) 2015 danigb.
 * SPDX-License-Identifier: MIT
 * Full license: tones/LICENSE
 */

@file:Suppress("DuplicatedCode")

package io.peekandpoke.klang.tones.pitch

/**
 * Creates a new [Pitch] instance from step, alteration, octave, and direction.
 */
fun Pitch(step: Int, alt: Int, oct: Int? = null, dir: Int? = null): Pitch =
    PitchImpl(step, alt, oct, dir)

/**
 * Represents a pitch with step, alteration, octave and direction.
 */
interface Pitch {
    /** The step number: 0 = C, 1 = D, ... 6 = B */
    val step: Int

    /** Number of alterations: -2 = 'bb', -1 = 'b', 0 = '', 1 = '#', ... */
    val alt: Int

    /** The octave (null for pitch classes) */
    val oct: Int?

    /** Interval direction (null for notes) */
    val dir: Int?

    companion object {
        /**
         * Returns the chroma of a pitch (0-11).
         */
        fun chroma(pitch: Pitch): Int = (SIZES[pitch.step] + pitch.alt + 120) % 12

        /**
         * Returns the height of a pitch.
         */
        fun height(pitch: Pitch): Int {
            val dir = pitch.dir ?: 1
            val oct = pitch.oct ?: -100
            return dir * (SIZES[pitch.step] + pitch.alt + 12 * oct)
        }

        /**
         * Returns the midi number of a pitch, or null if it's not a note.
         */
        fun midi(pitch: Pitch): Int? {
            val h = height(pitch)
            return if (pitch.oct != null && h >= -12 && h <= 115) h + 12 else null
        }

        /**
         * Get coordinates from pitch object
         */
        fun coordinates(pitch: Pitch): PitchCoordinates {
            val step = pitch.step
            val alt = pitch.alt
            val oct = pitch.oct
            val dir = pitch.dir ?: 1

            // Calculation of fifths based on step and alterations
            val f = FIFTHS[step] + 7 * alt

            if (oct == null) {
                return PitchCoordinates.PitchClass(dir * f)
            }

            // Calculation of octaves based on step and alterations
            val o = oct - STEPS_TO_OCTS[step] - 4 * alt

            return if (pitch.dir != null) {
                PitchCoordinates.Interval(dir * f, dir * o, dir)
            } else {
                PitchCoordinates.Note(dir * f, dir * o)
            }
        }

        /**
         * Get pitch from coordinate objects
         */
        fun pitch(coord: PitchCoordinates): Pitch {
            return when (coord) {
                is PitchCoordinates.PitchClass -> {
                    val f = coord.fifths
                    val step = FIFTHS_TO_STEPS[unaltered(f)]
                    val alt = kotlin.math.floor((f + 1).toDouble() / 7).toInt()
                    Pitch(step = step, alt = alt)
                }

                is PitchCoordinates.Note -> {
                    val f = coord.fifths
                    val o = coord.octaves
                    val step = FIFTHS_TO_STEPS[unaltered(f)]
                    val alt = kotlin.math.floor((f + 1).toDouble() / 7).toInt()
                    // Reconstruct octave from coordinates
                    val oct = o + 4 * alt + STEPS_TO_OCTS[step]
                    Pitch(step = step, alt = alt, oct = oct)
                }

                is PitchCoordinates.Interval -> {
                    val f = coord.fifths
                    val o = coord.octaves
                    val dir = coord.direction
                    val step = FIFTHS_TO_STEPS[unaltered(f)]
                    val alt = kotlin.math.floor((f + 1).toDouble() / 7).toInt()
                    // Reconstruct octave from coordinates
                    val oct = o + 4 * alt + STEPS_TO_OCTS[step]
                    Pitch(step = step, alt = alt, oct = oct, dir = dir)
                }
            }
        }

        /**
         * Returns true if the object is a [Pitch].
         */
        fun isPitch(src: Any?): Boolean {
            return src is Pitch
        }

        /**
         * Returns true if the object is a [NamedPitch].
         */
        fun isNamedPitch(src: Any?): Boolean {
            return src is NamedPitch
        }

        /**
         * Semitones from C for each step (0-6).
         */
        private val SIZES = intArrayOf(0, 2, 4, 5, 7, 9, 11)

        /**
         * The number of fifths of [C, D, E, F, G, A, B] from C.
         */
        private val FIFTHS = intArrayOf(0, 2, 4, -1, 1, 3, 5)

        /**
         * The number of octaves it spans for each step.
         */
        private val STEPS_TO_OCTS = FIFTHS.map { fifths ->
            kotlin.math.floor((fifths * 7).toDouble() / 12).toInt()
        }.toIntArray()

        /**
         * Reverse mapping from fifths to steps.
         */
        private val FIFTHS_TO_STEPS = intArrayOf(3, 0, 4, 1, 5, 2, 6)

        // Helper to get the unaltered step (0-6) from a number of fifths
        private fun unaltered(f: Int): Int {
            val i = (f + 1) % 7
            return if (i < 0) 7 + i else i
        }
    }
}

// Default implementation of Pitch interface
private data class PitchImpl(
    override val step: Int,
    override val alt: Int,
    override val oct: Int? = null,
    override val dir: Int? = null,
) : Pitch
